<template>
    <div class="hidden lg:flex w-full py-8 pb-6 items-start">
        <div class="flex items-center content-center gap-4 flex-1 flex-wrap">
            <!-- Program type filter -->
            <ButtonPopover buttonClass="btn-base btn-white !pl-4 !pr-3 h-10">
                <template #button-content>
                    <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">Program type</span>
                    <Transition name="fade">
                        <span
                            v-if="selectedProgramTypes.length >= 1"
                            class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                        >
                            {{ selectedProgramTypes.length }}
                        </span>
                    </Transition>
                    <Icon icon="chevron-down-small" class="w-5 h-5 text-slate-600 dark:text-slate-300" />
                </template>
                <template #default="{ closePopover }">
                    <ul>
                        <li v-for="option in programTypeOptions" :key="option.id">
                            <button
                                type="button"
                                @click="toggleProgramType(option.id)"
                                class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none flex items-center gap-2 text-slate-800 dark:text-slate-100"
                            >
                                <input type="checkbox" :checked="selectedProgramTypes.includes(option.id)" class="rounded text-10x-orange" readonly />
                                <span>{{ option.name }}</span>
                            </button>
                        </li>
                        <li v-if="selectedProgramTypes.length >= 1">
                            <button
                                type="button"
                                @click="clearProgramTypeFilters()"
                                class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none text-slate-800 dark:text-slate-100"
                            >
                                <span>Reset</span>
                            </button>
                        </li>
                    </ul>
                </template>
            </ButtonPopover>

            <!-- Added to My Points filter (current-rates tab only) -->
            <div v-if="currentTab === 'current-rates'">
                <ButtonPopover v-if="isSingleUser" buttonClass="btn-base btn-white !pl-4 !pr-3 h-10">
                    <template #button-content>
                        <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">Added to My Points</span>
                        <Icon icon="chevron-down-small" class="w-5 h-5 text-slate-600 dark:text-slate-300" />
                    </template>
                    <template #default="{ closePopover }">
                        <div class="flex p-2 items-center gap-6 rounded">
                            <span class="text-slate-800 dark:text-slate-100 text-sm leading-5 font-normal whitespace-nowrap">
                                Only programs added to My Points
                            </span>
                            <Switch
                                :value="singleUserToggle"
                                label="Only programs added to My Points"
                                @input="$emit('update:singleUserToggle', $event)"
                            />
                        </div>
                    </template>
                </ButtonPopover>

                <ButtonPopover v-else buttonClass="btn-base btn-white !pl-4 !pr-3 h-10">
                    <template #button-content>
                        <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">Added to My Points</span>
                        <Transition name="fade">
                            <span
                                v-if="selectedAddedToMyPoints.length >= 1"
                                class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                            >
                                {{ selectedAddedToMyPoints.length }}
                            </span>
                        </Transition>
                        <Icon icon="chevron-down-small" class="w-5 h-5 text-slate-600 dark:text-slate-300" />
                    </template>
                    <template #default="{ closePopover }">
                        <ul>
                            <li v-for="option in addedToMyPointsOptions" :key="option.id">
                                <button
                                    type="button"
                                    @click="toggleAddedToMyPointsSelection(option)"
                                    class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none flex items-center gap-2 text-slate-800 dark:text-slate-100"
                                >
                                    <input type="checkbox" :checked="isAddedToMyPointsSelected(option)" class="rounded text-10x-orange" readonly />
                                    <span>{{ option.name }}</span>
                                </button>
                            </li>
                            <li v-if="selectedAddedToMyPoints.length >= 1">
                                <button
                                    type="button"
                                    @click="clearAddedToMyPointsFilter()"
                                    class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700 focus:bg-gray-100 dark:focus:bg-slate-700 focus:outline-none text-slate-800 dark:text-slate-100"
                                >
                                    <span>Reset</span>
                                </button>
                            </li>
                        </ul>
                    </template>
                </ButtonPopover>
            </div>

            <!-- Bonus program filter (history tab only) -->
            <ButtonPopover
                v-if="currentTab === 'transfer-bonus-history'"
                buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
            >
                <template #button-content>
                    <span class="text-slate-800 dark:text-slate-100 text-sm font-medium">Bonus program</span>
                    <Transition name="fade">
                        <span
                            v-if="selectedBonusPrograms.length >= 1"
                            class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                        >
                            {{ selectedBonusPrograms.length }}
                        </span>
                    </Transition>
                    <Icon icon="chevron-down-small" class="w-5 h-5 text-slate-600 dark:text-slate-300" />
                </template>
                <template #default="{ closePopover }">
                    <div class="w-[360px] max-h-[360px]">
                        <div class="flex py-2.5 px-4 items-center gap-2 flex-1">
                            <Icon icon="search" class="w-5 h-5 text-slate-800 dark:text-slate-100" />
                            <input
                                type="text"
                                :value="bonusProgramSearchQuery"
                                @input="$emit('update:bonusProgramSearchQuery', $event.target.value)"
                                placeholder="Bonus program"
                                class="flex-1 text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 border-none outline-none bg-transparent p-0"
                            />
                            <button
                                v-if="bonusProgramSearchQuery"
                                @click="$emit('update:bonusProgramSearchQuery', '')"
                                class="flex items-center justify-center w-5 h-5 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:hover:text-slate-300"
                            >
                                <Icon icon="close" class="w-3 h-3" />
                            </button>
                        </div>
                        <div class="h-px bg-slate-200 dark:bg-slate-700"></div>
                        <div class="flex p-2 flex-col items-start max-h-[280px] overflow-y-auto scrollbar-overlay">
                            <ul class="flex items-start w-full">
                                <div class="flex flex-col items-start w-full">
                                    <li
                                        v-if="filteredBonusProgramOptions.length === 0 && bonusProgramSearchQuery"
                                        class="flex items-start w-full"
                                    >
                                        <div class="flex p-2 items-center gap-3 flex-1">
                                            <span class="text-slate-900 dark:text-slate-100 text-sm leading-5 font-normal">
                                                No results found, try adjusting your search
                                            </span>
                                        </div>
                                    </li>
                                    <li v-for="option in filteredBonusProgramOptions" :key="option.id" class="flex items-start w-full">
                                        <button
                                            type="button"
                                            @click="toggleBonusProgram(option.id)"
                                            class="flex p-2 items-center gap-3 flex-1 rounded hover:bg-gray-50 dark:hover:bg-slate-700 focus:bg-gray-50 dark:focus:bg-slate-700 focus:outline-none"
                                        >
                                            <input type="checkbox" :checked="isBonusProgramSelected(option.id)" class="rounded text-10x-orange" readonly />
                                            <span class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5">{{ option.name }}</span>
                                        </button>
                                    </li>
                                    <li
                                        v-if="selectedBonusPrograms.length >= 1 && filteredBonusProgramOptions.length > 0"
                                        class="flex items-start w-full border-t border-slate-200 dark:border-slate-700 mt-1 pt-1"
                                    >
                                        <button
                                            type="button"
                                            @click="clearBonusProgramFilters()"
                                            class="flex p-2 items-center gap-3 flex-1 rounded hover:bg-gray-50 dark:hover:bg-slate-700 focus:bg-gray-50 dark:focus:bg-slate-700 focus:outline-none"
                                        >
                                            <span class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5">Reset</span>
                                        </button>
                                    </li>
                                </div>
                            </ul>
                        </div>
                    </div>
                </template>
            </ButtonPopover>

            <!-- Reset all -->
            <button
                v-if="isAnyFilterApplied"
                class="btn-base btn-transparent !pl-4 !pr-3 !py-1.5"
                @click="clearAllFilters"
            >
                <span class="text-primary text-sm font-medium">Reset all</span>
                <Icon icon="x" class="w-3 h-3 icons-primary" />
            </button>

            <!-- Bonus alert button (toolbar) -->
            <div v-if="alertsFeatureEnabled" class="relative ml-auto">
                <button
                    @click="$emit('update:openBonusAlert', openBonusAlert === 'toolbar-desktop' ? null : 'toolbar-desktop')"
                    class="bonus-alert-button flex h-10 py-2.5 px-4 items-center gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                    <Icon
                        :icon="bonusAlertEnabled ? 'bell-added-color' : 'alert'"
                        class="w-4 h-4 text-slate-800 dark:text-slate-100"
                    />
                    <span class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5">
                        {{ bonusAlertEnabled ? 'Bonus alert enabled' : 'Add bonus alert' }}
                    </span>
                </button>

                <div
                    v-if="openBonusAlert === 'toolbar-desktop'"
                    class="hidden md:block bonus-alert-dropdown absolute right-0 top-12 w-[264px] p-4 flex flex-col items-start bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50"
                >
                    <BonusAlertDropdown
                        :programName="programName"
                        :email="email"
                        :bonusAlertEnabled="bonusAlertEnabled"
                        :bonusAlertLoading="bonusAlertLoading"
                        variant="desktop"
                        @toggle="$emit('toggle-bonus-alert', $event)"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import ButtonPopover from '@/components/ui/button-popover.vue';
import Switch from '@/components/ui/switch/switch.vue';
import BonusAlertDropdown from './BonusAlertDropdown.vue';

defineProps({
    currentTab: String,
    selectedProgramTypes: Array,
    programTypeOptions: Array,
    selectedAddedToMyPoints: Array,
    addedToMyPointsOptions: Array,
    isSingleUser: Boolean,
    singleUserToggle: Boolean,
    selectedBonusPrograms: Array,
    bonusProgramSearchQuery: String,
    filteredBonusProgramOptions: Array,
    isAnyFilterApplied: Boolean,
    alertsFeatureEnabled: Boolean,
    bonusAlertEnabled: Boolean,
    bonusAlertLoading: Boolean,
    openBonusAlert: [String, null],
    programName: String,
    email: String,
    toggleProgramType: Function,
    clearProgramTypeFilters: Function,
    toggleAddedToMyPointsSelection: Function,
    isAddedToMyPointsSelected: Function,
    clearAddedToMyPointsFilter: Function,
    toggleBonusProgram: Function,
    isBonusProgramSelected: Function,
    clearBonusProgramFilters: Function,
    clearAllFilters: Function,
});

defineEmits([
    'update:singleUserToggle',
    'update:bonusProgramSearchQuery',
    'update:openBonusAlert',
    'toggle-bonus-alert',
]);
</script>
